import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'
import { toast } from 'sonner'
import { BizError } from '@/types/api'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { getValidAiGuestToken } from '@/features/ai-doc/guest-access'
import {
  decryptBase64,
  decryptWithAes,
  decryptWithRsa,
  encryptBase64,
  encryptWithAes,
  encryptWithRsa,
  generateAesKey,
} from './request-crypto'

/**
 * axios 实例
 *
 * 拦截器策略:
 * 1. 请求: 自动注入 Authorization Bearer 与 clientId 头
 * 2. 响应:
 *    - 二进制流 (blob/arraybuffer) 原样返回
 *    - 形如 TableDataInfo (含 rows + total) 保留结构,调用方按 .rows / .total 取值
 *    - 形如 R<T> (仅含 code/msg/data) 解包返回 data 字段
 *    - code !== 200 抛 BizError
 *    - HTTP 401 清 token + 跳登录; 其他错误 toast 提示
 */
export const apiBaseURL = (import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/$/, '')
const encryptHeader = import.meta.env.VITE_HEADER_FLAG || 'encrypt-key'
const encryptEnabled = import.meta.env.VITE_APP_ENCRYPT === 'Y'

const instance: AxiosInstance = axios.create({
  // 开发默认请求 /api/*,由 vite proxy 转发到后端;生产默认由 nginx 反代同一前缀
  // 仅当前端需要直连独立 API 域名时,才把 VITE_API_BASE_URL 改为完整地址
  baseURL: apiBaseURL,
  timeout: 30_000,
})

instance.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken
  if (token && shouldAttachAuthHeader(config.url)) {
    const authToken = normalizeAuthHeaderToken(token)
    if (authToken) {
      config.headers.set('Authorization', `Bearer ${authToken}`)
    }
  }
  const clientId = import.meta.env.VITE_AUTH_CLIENT_ID
  if (clientId) {
    config.headers.set('clientid', clientId)
  }
  const guestToken = getValidAiGuestToken()
  if (guestToken && shouldAttachAiGuestToken(config.url)) {
    config.headers.set('x-ai-guest-token', guestToken)
  }
  handleEncryptedRequest(config)
  return config
})

/** 后台预准备可抑制错误提示，但不绕过认证恢复逻辑。 */
export type HttpOptions = AxiosRequestConfig & { silentError?: boolean }
function isSilent(config?: AxiosRequestConfig) {
  return (config as HttpOptions | undefined)?.silentError === true
}

interface BackendEnvelope {
  code?: number | string
  msg?: string
  data?: unknown
  rows?: unknown
  total?: number | string
}

instance.interceptors.response.use(
  (res) => {
    if (res.config.responseType === 'blob' || res.config.responseType === 'arraybuffer') {
      return res.data
    }

    const payload = decryptEncryptedResponse(res.data, res.headers) as BackendEnvelope | undefined

    if (payload && typeof payload === 'object') {
      const code = normalizeBackendCode(payload.code)
      if (code === undefined) return payload
      if (code === 200) {
        if ('rows' in payload && 'total' in payload) return payload
        return payload.data
      }
      return rejectBackendBusinessError(code, payload.msg, isSilent(res.config))
    }

    return payload
  },
  (err: AxiosError<BackendEnvelope>) => {
    const status = err.response?.status
    const msg = err.response?.data?.msg ?? err.message ?? '请求失败'

    if (status === 401) {
      useAuthStore.getState().logout()
      useUiStore.getState().openLoginModal()
      if (!isSilent(err.config)) toast.error('登录已过期，请重新登录')
      return Promise.reject(new BizError(401, msg))
    }

    if (err instanceof BizError) {
      if (!isSilent(err.config)) toast.error(err.message)
      return Promise.reject(err)
    }

    if (!isSilent(err.config)) toast.error(msg)
    return Promise.reject(new BizError(status ?? 500, msg))
  },
)

function handleEncryptedRequest(config: InternalAxiosRequestConfig) {
  const isEncrypt = String(config.headers.get('isEncrypt') ?? '') === 'true'
  config.headers.delete('isEncrypt')

  if (!encryptEnabled || !isEncrypt || !isEncryptedMethod(config.method)) return

  const publicKey = import.meta.env.VITE_APP_RSA_PUBLIC_KEY?.trim()
  if (!publicKey) {
    throw new Error('未配置请求加密公钥 VITE_APP_RSA_PUBLIC_KEY')
  }

  const aesKey = generateAesKey()
  config.headers.set('Content-Type', 'application/json')
  config.headers.set(encryptHeader, encryptWithRsa(encryptBase64(aesKey), publicKey))
  config.data = encryptWithAes(stringifyRequestBody(config.data), aesKey)
}

function decryptEncryptedResponse(data: unknown, headers: unknown): unknown {
  const encryptedKey = readHeader(headers, encryptHeader)
  if (!encryptEnabled || !encryptedKey) return data

  const privateKey = import.meta.env.VITE_APP_RSA_PRIVATE_KEY?.trim()
  if (!privateKey) {
    throw new Error('未配置响应解密私钥 VITE_APP_RSA_PRIVATE_KEY')
  }

  const aesKey = decryptBase64(decryptWithRsa(encryptedKey, privateKey))
  const decrypted = decryptWithAes(String(data), aesKey)
  return JSON.parse(decrypted)
}

function normalizeBackendCode(code: unknown): number | undefined {
  if (typeof code === 'number') return code
  if (typeof code !== 'string') return undefined

  const normalized = Number(code.trim())
  return Number.isFinite(normalized) ? normalized : undefined
}

function rejectBackendBusinessError(code: number, msg?: string, silent = false) {
  if (code === 401) {
    useAuthStore.getState().logout()
    useUiStore.getState().openLoginModal()
  }

  const message = msg ?? (code === 401 ? '登录已过期，请重新登录' : '操作失败')
  const error = new BizError(code, message)
  if (!silent) toast.error(error.message)
  return Promise.reject(error)
}

function isEncryptedMethod(method: string | undefined): boolean {
  return method === 'post' || method === 'put'
}

function stringifyRequestBody(data: unknown): string {
  if (typeof data === 'string') return data
  return JSON.stringify(data ?? {})
}

function readHeader(headers: unknown, name: string): string | undefined {
  if (!headers || typeof headers !== 'object') return undefined

  const record = headers as {
    get?: (headerName: string) => unknown
    [key: string]: unknown
  }
  const getterValue = record.get?.(name)
  const value = getterValue ?? record[name] ?? record[name.toLowerCase()]
  return value == null ? undefined : String(value)
}

function normalizeAuthHeaderToken(token: string): string | null {
  const cleaned = token
    .trim()
    .replace(/^Bearer\s+/i, '')
    .trim()

  if (!cleaned || hasHeaderControlChars(cleaned)) return null
  return cleaned
}

function hasHeaderControlChars(value: string): boolean {
  return Array.from(value).some((char) => {
    const code = char.charCodeAt(0)
    return code <= 31 || code === 127
  })
}

const anonymousRequestPaths = new Set([
  '/auth/code',
  '/app/auth/methods',
  '/app/auth/login',
  '/app/auth/sms/code',
  '/app/auth/wechat/url',
  '/app/auth/wechat/qr/code',
  '/app/auth/wechat/qr/status',
  '/app/legal/ai/access/guest',
])
const apiBasePath = getPathname(apiBaseURL)

function shouldAttachAuthHeader(url: string | undefined): boolean {
  const path = normalizeApiPath(getPathname(url ?? ''))
  return !anonymousRequestPaths.has(path)
}

function shouldAttachAiGuestToken(url: string | undefined): boolean {
  const path = normalizeApiPath(getPathname(url ?? ''))
  return (
    (path.startsWith('/app/legal/ai/') && path !== '/app/legal/ai/access/guest') ||
    path === '/app/legal/wechat/jssdk-config' ||
    path === '/app/legal/wechat/image' ||
    path.startsWith('/client/legal/ai/document/preview/')
  )
}

function normalizeApiPath(path: string): string {
  if (apiBasePath !== '/' && path.startsWith(`${apiBasePath}/`)) {
    return path.slice(apiBasePath.length)
  }
  return path
}

function getPathname(url: string): string {
  if (!url) return ''
  try {
    return new URL(url, window.location.origin).pathname
  } catch {
    return url.split('?')[0] ?? ''
  }
}

/**
 * 类型友好的 http helper
 * 第二泛型替换 axios 默认的 AxiosResponse<T>, 直接返回解包后的业务数据类型
 *
 * @example
 *   const user = await http.get<UserVo>('/system/user/1')        // 解包 R<UserVo>.data
 *   const page = await http.get<TableDataInfo<UserVo>>('/list')  // 列表保持 { rows, total }
 */
export const http = {
  get: <T>(url: string, cfg?: HttpOptions) => instance.get<unknown, T>(url, cfg),
  post: <T>(url: string, body?: unknown, cfg?: HttpOptions) =>
    instance.post<unknown, T>(url, body, cfg),
  put: <T>(url: string, body?: unknown, cfg?: HttpOptions) =>
    instance.put<unknown, T>(url, body, cfg),
  patch: <T>(url: string, body?: unknown, cfg?: HttpOptions) =>
    instance.patch<unknown, T>(url, body, cfg),
  delete: <T>(url: string, cfg?: HttpOptions) => instance.delete<unknown, T>(url, cfg),
}

export { instance as axiosInstance }
